// ============================================================================
// Card Component
// Feature: 099-app-review-optimization
// Created: 2026-01-11
// Description: Base card component for content containers
// ============================================================================

import React, { forwardRef, HTMLAttributes, ReactNode } from 'react';
import { clsx } from 'clsx';

/**
 * Card variants
 */
export type CardVariant = 'default' | 'outlined' | 'elevated' | 'flat';

/**
 * Card padding sizes
 */
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

/**
 * Card props
 */
/**
 * 🛑 `title` IS OMITTED FROM THE DIV ATTRIBUTES ON PURPOSE.
 *
 * `HTMLAttributes<HTMLDivElement>` carries `title?: string`, so `<Card title="Problems">`
 * typechecked, spread onto the div, and rendered as a hover TOOLTIP — the heading never
 * appeared. Eighteen call sites across seven files (Spec 206/254 monitoring, Spec 265
 * integrity, Infrastructure Sync) had all written it that way and every one rendered an
 * anonymous box. Nothing caught it: the prop is valid HTML, so tsc is happy, and
 * design-conformance checks tokens and kit imports, not whether a prop does what its name
 * says.
 *
 * Re-declaring `title` as a heading makes the intended reading the only reading — passing
 * it can no longer silently produce a tooltip. For a real tooltip use `AppTooltip`.
 */
export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  variant?: CardVariant;
  padding?: CardPadding;
  isHoverable?: boolean;
  isClickable?: boolean;
  /** Section heading. Renders a CardHeader — never the HTML title attribute. */
  title?: ReactNode;
  /** Supporting line under the heading. Requires `title`. */
  subtitle?: ReactNode;
  /** Right-aligned control in the heading row (filter, link, button). */
  action?: ReactNode;
  children: ReactNode;
}

/**
 * Base styles for all cards
 */
const baseStyles = 'rounded-[var(--radius-lg)] transition-shadow duration-[180ms]';

/**
 * Variant styles
 */
// Spec 197 design blueprint: token-driven surfaces (Azure light + dark) — no
// per-variant dark: overrides; the tokens carry both modes.
const variantStyles: Record<CardVariant, string> = {
  default: `
    bg-[var(--color-bg-card)] border border-[var(--color-border)] shadow-[var(--shadow-md)]
  `,
  outlined: `
    bg-transparent border border-[var(--color-border)]
  `,
  elevated: `
    bg-[var(--color-bg-card)] border border-[var(--color-border)] shadow-[var(--shadow-lg)]
  `,
  flat: `
    bg-[var(--color-bg-subtle)]
  `,
};

/**
 * Padding styles
 */
const paddingStyles: Record<CardPadding, string> = {
  none: '',
  sm: 'p-3.5',
  md: 'p-5',
  lg: 'p-6',
};

/**
 * Card Component
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'md',
      isHoverable = false,
      isClickable = false,
      title,
      subtitle,
      action,
      children,
      className,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={clsx(
          baseStyles,
          variantStyles[variant],
          paddingStyles[padding],
          isHoverable && 'hover:shadow-[var(--shadow-lg)]',
          isClickable && 'cursor-pointer',
          className
        )}
        {...props}
      >
        {(title || subtitle || action) && (
          <CardHeader title={title} subtitle={subtitle} action={action} />
        )}
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

/**
 * Card Header Component
 */
export interface CardHeaderProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title'> {
  title?: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  children?: ReactNode;
}

export const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ title, subtitle, action, children, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx('flex items-start justify-between mb-4', className)}
        {...props}
      >
        {children || (
          <>
            <div>
              {title && (
                <h3 className="text-lg font-semibold text-[var(--color-text-primary)]">{title}</h3>
              )}
              {subtitle && (
                <p className="text-sm text-[var(--color-text-secondary)] mt-1">{subtitle}</p>
              )}
            </div>
            {action && <div>{action}</div>}
          </>
        )}
      </div>
    );
  }
);

CardHeader.displayName = 'CardHeader';

/**
 * Card Body Component
 */
export interface CardBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <div ref={ref} className={clsx('', className)} {...props}>
        {children}
      </div>
    );
  }
);

CardBody.displayName = 'CardBody';

/**
 * Card Footer Component
 */
export interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

export const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ children, className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={clsx(
          'mt-4 pt-4 border-t border-[var(--color-border)]',
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

CardFooter.displayName = 'CardFooter';

export default Card;
